import { useEffect, useState } from "react";
import { api } from "../api.js";
import { useApp } from "../app.jsx";
import { navigate, Link } from "../router.jsx";
import { money, fmtDate, daysUntil } from "../format.js";
import {
  Async, Badge, Button, Card, ErrorPanel, Field, Fields, Grid, Input, Modal, Ribbon, Sep, TopBar,
  Wrap, useAsync, useToast,
} from "../ui.jsx";
import DocumentView from "../components/DocumentView.jsx";
import IssuerLogo from "../components/IssuerLogo.jsx";

export default function EstimateDetail({ id }) {
  const { settings, organization, reloadApp, me } = useApp();
  const canManage = !!me.permissions["estimates.manage"];
  const toast = useToast();
  const state = useAsync(() => api.get(`estimates/${id}`), [id]);
  const [busy, setBusy] = useState(null); // which action is running
  const [sendOpen, setSendOpen] = useState(false);
  const [error, setError] = useState(null);

  const est = state.data;
  const sym = est?.currency_symbol || settings.currency_symbol;
  const decided = est ? ["accepted", "declined"].includes(est.status) : false;

  async function act(kind, message) {
    setError(null);
    setBusy(kind);
    try {
      await api.post(`estimates/${id}/${kind}`, {});
      toast.push(message);
      state.reload();
      reloadApp();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  }

  async function convert() {
    setError(null);
    setBusy("convert");
    try {
      const invoice = await api.post(`estimates/${id}/convert`, {});
      toast.push(`${invoice.number} created from this estimate`);
      reloadApp();
      navigate(`/invoices/${invoice.id}`);
    } catch (e) {
      setError(e);
      setBusy(null);
    }
  }

  return (
    <>
      <TopBar crumb="Get paid / Estimates" title={est ? est.number : "Estimate"}>
        <Button onClick={() => api.openBlob(api.estimatePdfPath(id)).catch((e) => toast.push(e.message, "err"))}>
          ⤓ PDF
        </Button>
        {est && !decided && canManage && (
          <Button kind="pri" onClick={() => setSendOpen(true)}>
            {est.status === "draft" ? "Send estimate" : "Resend estimate"}
          </Button>
        )}
        {est && est.status === "accepted" && !est.converted_invoice_id && canManage && (
          <Button kind="pri" busy={busy === "convert"} onClick={convert}>
            Convert to invoice
          </Button>
        )}
      </TopBar>

      <Wrap>
        {error && <ErrorPanel error={error} />}
        <Async state={state}>
          {(e) => {
            const expiresIn = daysUntil(e.expires_on);
            return (
              <Grid cols="1fr 322px" style={{ alignItems: "start" }}>
                <DocumentView
                  doc={e}
                  kind="estimate"
                  company={est.issuer || organization || settings}
                  logo={
                    <IssuerLogo issuer={est.issuer || organization || settings} live={organization || settings} />
                  }
                  client={{
                    name: e.client_name,
                    contact_name: e.contact_name,
                    address: e.client_address,
                    email: e.client_email,
                  }}
                />

                <div style={{ display: "grid", gap: 14 }}>
                  <Card title="Estimate">
                    <div style={{ display: "grid", gap: 9 }}>
                      <div className="row">
                        <span className="muted sm">Status</span>
                        <div className="grow" />
                        <Badge status={e.status} />
                      </div>
                      <div className="row">
                        <span className="muted sm">Total</span>
                        <div className="grow" />
                        <b className="num">{money(e.total, sym)}</b>
                      </div>
                      <div className="row">
                        <span className="muted sm">Expires</span>
                        <div className="grow" />
                        <span className="sm">{fmtDate(e.expires_on)}</span>
                      </div>
                      <Sep />
                      {e.converted_invoice_id ? (
                        <Ribbon>
                          Converted —{" "}
                          <Link to={`/invoices/${e.converted_invoice_id}`} className="linky">
                            open the invoice
                          </Link>
                        </Ribbon>
                      ) : !decided && e.expires_on && expiresIn < 0 ? (
                        <Ribbon tone="warn">
                          This estimate expired {Math.abs(expiresIn)} day{Math.abs(expiresIn) === 1 ? "" : "s"} ago.
                        </Ribbon>
                      ) : (
                        <div className="tiny muted">
                          Clients can accept or decline from the link they were emailed. Accepted estimates can be
                          converted into an invoice in one click.
                        </div>
                      )}
                    </div>
                  </Card>

                  {!decided && canManage && (
                    <Card title="Client decision">
                      <div style={{ display: "grid", gap: 8 }}>
                        <Button
                          style={{ width: "100%", justifyContent: "center" }}
                          busy={busy === "accept"}
                          onClick={() => act("accept", `${e.number} marked accepted`)}
                        >
                          Mark as accepted
                        </Button>
                        <Button
                          kind="dgr"
                          style={{ width: "100%", justifyContent: "center" }}
                          busy={busy === "decline"}
                          onClick={() => act("decline", `${e.number} marked declined`)}
                        >
                          Mark as declined
                        </Button>
                        <div className="tiny muted">
                          Record the answer here if the client replies by phone or email instead.
                        </div>
                      </div>
                    </Card>
                  )}

                  {e.status === "declined" && (
                    <Card title="Declined">
                      <div className="tiny muted">
                        This estimate was declined, so it can no longer be edited or converted. Create a new estimate
                        to quote a revised scope.
                      </div>
                    </Card>
                  )}
                </div>
              </Grid>
            );
          }}
        </Async>
      </Wrap>

      {sendOpen && est && (
        <SendEstimateModal
          estimate={est}
          onClose={() => setSendOpen(false)}
          onSent={() => {
            state.reload();
            reloadApp();
          }}
        />
      )}
    </>
  );
}

/* -------------------------------------------------------------- send modal */

function SendEstimateModal({ estimate, onClose, onSent }) {
  const toast = useToast();
  const [form, setForm] = useState({ to: "", subject: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setForm({
      to: estimate.client_email || "",
      subject: `Estimate ${estimate.number} — ${money(estimate.total, estimate.currency_symbol)}`,
    });
  }, [estimate.id]);

  async function send() {
    setError(null);
    setBusy(true);
    try {
      const res = await api.post(`estimates/${estimate.id}/send`, form);
      toast.push(res.mail?.skipped ? "Estimate marked sent (email is not configured yet)" : "Estimate sent");
      onSent();
      onClose();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Send estimate"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button kind="pri" busy={busy} onClick={send}>
            Send estimate
          </Button>
        </>
      }
    >
      <Fields>
        {error && <ErrorPanel error={error} />}
        <Field label="To">
          <Input value={form.to} placeholder="client@example.com" onChange={(e) => setForm({ ...form, to: e.target.value })} />
        </Field>
        <Field label="Subject" hint="The email includes a link where the client can accept or decline.">
          <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
        </Field>
      </Fields>
    </Modal>
  );
}
